import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { mediaStore } from '~/stores';
import { usePlatform } from '~/services/platform';
import type {
  InteractiveElement,
  MediaFitMode,
} from '~/stores/interactiveElements.store';

interface Props {
  element: InteractiveElement;
}

const Wrapper = styled.div<{ $x: number; $y: number; $w: number; $h: number }>`
  position: absolute;
  left: ${({ $x }) => $x}%;
  top: ${({ $y }) => $y}%;
  width: ${({ $w }) => $w}%;
  height: ${({ $h }) => $h}%;
  pointer-events: none; /* display-only — sobrepõe o conteúdo, não captura toque */
  overflow: hidden;
`;

/**
 * SEM background — qualquer transparência da mídia (PNG/WebM com alpha, ex.:
 * moldura) deixa o conteúdo principal aparecer por baixo. `object-fit` segue
 * o modo escolhido pelo operador (fill = estica, contain = inteiro, cover =
 * preenche cortando).
 */
const Media = styled.div<{ $fit: MediaFitMode }>`
  width: 100%;
  height: 100%;

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: ${({ $fit }) => $fit};
    display: block;
  }
`;

/**
 * Conteúdo sobreposto (imagem/vídeo) — 4º tipo de elemento interativo.
 * Display-only (sem clique). Placement (x/y/w/h, opacidade, janela de tempo)
 * vem do elemento; a mídia é outro conteúdo já enviado, presigned pelo server
 * em `element.mediaUrl`. Vídeo = mudo + loop (decisão Renato). Transparência
 * nativa via ausência de background.
 *
 * Overlay de IMAGEM (`<img>`) renderiza em TODAS as plataformas (como Clima/QR).
 *
 * Overlay de VÍDEO: renderiza em Android/Browser, mas NÃO no Tizen/webOS (TV).
 * GUARDRAIL (conteudo-interativo.md + plataformas-suportadas.md): a TV tem 1 só
 * decoder de vídeo HW e o `<video muted loop>` do overlay disputava com o
 * `<video>` PRINCIPAL do conteúdo → o principal ficava preso em readyState=0
 * (preto / "Buffering..." eterno) e o slot nunca avançava. Poster-via-canvas é
 * inviável na TV (readback de `<video>` no WebKit Tizen volta preto) e QUALQUER
 * 2º `<video>` no DOM já compete pelo decoder mesmo pausado. Decisão: no TV o
 * overlay de vídeo simplesmente NÃO aparece (limitação conhecida aceita).
 */
const InteractiveMediaOverlay: React.FC<Props> = observer(({ element }) => {
  const platform = usePlatform();
  const [isActive, setIsActive] = useState(element.inSeconds <= 0);

  // URL efetiva: cache local (offline + instantâneo) com FALLBACK pra rede
  // (presigned). Começa VAZIA e só pinta depois de resolver.
  //
  // ⚠️ NÃO voltar a inicializar com `element.mediaUrl`: a presigned do R2
  // rotaciona a cada sync, então semear o estado com ela fazia CADA montagem do
  // overlay (uma por exibição do conteúdo) pintar um `<img src>` com uma URL
  // INÉDITA. O renderer não deduplica URLs diferentes do mesmo objeto → cada
  // exibição disparava um fetch HTTPS novo e retinha mais uma cópia decodificada
  // (VA do renderer 32-bit cresce sem teto → renderer-kill no embedded).
  // Resolver primeiro custa 1 microtask: no MISS o `getLocalUrlAsync` retorna
  // null de imediato (sem entrada 'cached' no store) e caímos na rede na mesma
  // hora. Vide `.claude/rules/cache-offline-first.md` — local sempre, rede é
  // fallback, nunca o caminho feliz.
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);

  // Janela de tempo (in/out segundos) — mesmo padrão do InteractiveWeather.
  useEffect(() => {
    const timers: number[] = [];
    if (element.inSeconds > 0) {
      timers.push(
        window.setTimeout(() => setIsActive(true), element.inSeconds * 1000),
      );
    }
    if (element.outSeconds !== null && element.outSeconds > 0) {
      timers.push(
        window.setTimeout(() => setIsActive(false), element.outSeconds * 1000),
      );
    }
    return () => timers.forEach(clearTimeout);
  }, [element.inSeconds, element.outSeconds]);

  // Resolve a URL: cache-first (blob/file local), senão a presigned remota.
  // Best-effort — qualquer falha cai pra rede. Keya por mediaContentId
  // (estável); media_url muda a cada sync (presigned) mas é usada só como
  // fallback fresco. Revoga o blob no cleanup pra não vazar.
  useEffect(() => {
    let alive = true;
    let createdBlob: string | null = null;
    const id = element.mediaContentId;
    const remote = element.mediaUrl;
    if (!id || !remote) {
      setResolvedUrl(remote ?? null);
      return;
    }
    mediaStore
      .getLocalUrlAsync({
        id,
        name: element.labelInterno || 'overlay',
        url: remote,
      })
      .then((local) => {
        if (!alive) {
          if (local && local.startsWith('blob:')) URL.revokeObjectURL(local);
          return;
        }
        if (local && local.startsWith('blob:')) createdBlob = local;
        setResolvedUrl(local ?? remote);
      })
      .catch(() => {
        if (alive) setResolvedUrl(remote);
      });
    return () => {
      alive = false;
      if (createdBlob) URL.revokeObjectURL(createdBlob);
    };
  }, [element.mediaContentId, element.mediaUrl, element.labelInterno]);

  if (!element.mediaUrl) return null; // sem URL → esconde gracioso

  const isVideo = element.mediaType === 'video';
  const isTV = platform.isTV();

  // Overlay de VÍDEO na TV NÃO renderiza (só 1 <video> pode decodificar na TV —
  // o principal). Overlay de imagem segue normal. Vide comentário do componente.
  if (isVideo && isTV) return null;

  // Ainda resolvendo (1 microtask) → não pinta nada. Pintar a presigned aqui
  // como placeholder é exatamente o que vazava (vide comentário do useState).
  if (!resolvedUrl) return null;

  const src = resolvedUrl;

  return (
    <Wrapper
      $x={element.xPct}
      $y={element.yPct}
      $w={element.wPct}
      $h={element.hPct}
      style={{ opacity: isActive ? element.opacity : 0 }}
    >
      <Media $fit={element.mediaFitMode}>
        {isVideo ? (
          <video src={src} autoPlay loop muted playsInline />
        ) : (
          <img src={src} alt="" />
        )}
      </Media>
    </Wrapper>
  );
});

export default InteractiveMediaOverlay;
